/**
 * Brand marks for activation destination types, plus the generic fallback. SVGs
 * come from `@rudderlabs/icons`, the integration icon library the webapp also uses;
 * rendered through `PlatedBrandMark` with baked-in brand colors — same exception, and the
 * same dark-mode logo plate, as `WarehouseTypeIcon`.
 *
 * Resolve these through `destinationTypeIcon()` (`./destinationTypes`) rather
 * than importing a mark directly: the definition name is a backend string, and
 * the registry is what guarantees an unrecognised one still gets a glyph. This
 * file is glyphs only — it exports no plain functions, so the registry beside it
 * exports no components (both halves of `react-refresh/only-export-components`).
 */
import { type IconName, iconUrl } from '@rudderlabs/icons';
import { Send } from 'lucide-react';
import * as React from 'react';

import { PlatedBrandMark } from '@/_shared/components/PlatedBrandMark';
import { cn } from '@/_shared/lib/utils';

interface IconProps {
  className?: string;
}

/**
 * A brand mark in a SQUARE box, letterboxed rather than stretched.
 *
 * `@rudderlabs/icons` draws every mark on a square 24×24 artboard, Braze's wordmark included. The
 * old local Braze asset was a bare 1011×467 wordmark, and an `<img>` given equal width and height
 * fills to both, so it was crushed to 46% of its height (an illegible smudge at the account drawer
 * badge's 14px). `object-contain` (on `PlatedBrandMark`'s `<img>`) stays as the guard for any
 * non-square asset: it sits at its true proportions inside the box, with space above and below.
 *
 * The box stays SQUARE deliberately, rather than letting the width follow the asset. These marks sit
 * at the head of rows, cards and badges, and a 35px-wide Braze beside a 16px-wide Meta would start
 * every neighbouring label at a different x — the list reads ragged down its whole length, which is
 * the same fault the account row's reserved check slot exists to prevent. A uniform box is worth more
 * than a few extra pixels of wordmark.
 */
function BrandIcon({ name, className }: { name: IconName; className?: string }): React.JSX.Element {
  return <PlatedBrandMark src={iconUrl(name)} className={cn('size-5', className)} />;
}

export function MetaAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="meta" className={className} />;
}

export function TikTokAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="tiktok" className={className} />;
}

export function IterableAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="iterable" className={className} />;
}

export function LinkedInAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="linkedin" className={className} />;
}

export function GoogleAdsAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="google-ads" className={className} />;
}

export function BrazeAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="braze" className={className} />;
}

export function RedditAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="reddit" className={className} />;
}

export function HubSpotAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="hubspot" className={className} />;
}

export function MarketoAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="marketo" className={className} />;
}

export function PinterestAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="pinterest" className={className} />;
}

export function GainsightPxAudienceIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="gainsight-px" className={className} />;
}

/**
 * The mark for a destination we ship no brand asset for. `Send` is already the
 * destination glyph on `ActivationCard`'s meta row and the activations empty
 * state, so an unrecognised type reads as "a destination" rather than as a
 * rendering bug — which a blank slot or a broken `<img>` would.
 */
export function GenericDestinationIcon({ className }: IconProps): React.JSX.Element {
  return <Send aria-hidden className={cn('size-5 shrink-0 text-muted-foreground', className)} />;
}
